import React, { useEffect, useState } from "react";
import { DashboardViewModel } from "../../viewmodels/dashboard/DashboardViewModel";

interface DashboardProps {
  logout: () => void;
}

interface MetricCard {
  title: string;
  value: string | number;
  change: string;
  changeType: 'positive' | 'negative' | 'neutral';
  icon: React.ReactNode;
  color: string;
}

interface ActivityItem {
  type: string;
  message: string;
  time: string;
  time_ago: string;
}

interface DashboardApiResponse {
  metrics: {
    total_sales: number;
    orders_today: number;
    low_stock_count: number;
    active_orders: number;
  };
  chart_data: {
    labels: string[];
    data: number[];
  };
  top_items: Array<{
    name: string;
    sales: number;
  }>;
  top_customers: Array<{
    name: string;
    orders: number;
  }>;
  recent_activity: ActivityItem[];
}

const Dashboard: React.FC<DashboardProps> = ({ logout }) => {
  const {
    getStatsSummaries,
    getProductInventoryStats,
    getTopSellingItemsStats,
    fetchTopFiveSalesPersons,
    getChartData,
    statsSummaries,
    productInventoryStats,
    topSellingItemsStats,
    topSalesPersonsStats,
    chartData,
  } = DashboardViewModel(logout);

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardApiResponse | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Use the existing viewmodel functions
        await Promise.all([
          getStatsSummaries(),
          getProductInventoryStats(),
          getTopSellingItemsStats(),
          fetchTopFiveSalesPersons(),
          getChartData(),
        ]);

        // Also try to fetch the new dashboard summary API
        try {
          const response = await fetch('/api/dashboard/summary', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            setDashboardData(data);
          }
        } catch (apiError) {
          console.log('Dashboard summary API not available, using existing data');
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // Empty dependency array to run only once on mount

  const userName = localStorage.getItem("first_name");
  const userType = localStorage.getItem("userType");

  // Transform API data into metrics cards - use new API data if available, otherwise use existing viewmodel data
  const metrics: MetricCard[] = dashboardData ? [
    {
      title: "Total Sales",
      value: `$${dashboardData.metrics.total_sales.toLocaleString()}`,
      change: "+12% vs LM", // This would come from API in real implementation
      changeType: "positive",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
      color: "bg-blue-500"
    },
    {
      title: "Orders Today",
      value: dashboardData.metrics.orders_today.toString(),
      change: "+3 vs LY", // This would come from API in real implementation
      changeType: "positive",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      color: "bg-green-500"
    },
    {
      title: "Low Stock",
      value: dashboardData.metrics.low_stock_count.toString(),
      change: "⚠️ Alert",
      changeType: "negative",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
      color: "bg-orange-500"
    },
    {
      title: "Active Orders",
      value: dashboardData.metrics.active_orders.toString(),
      change: "📋 Pending",
      changeType: "neutral",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: "bg-purple-500"
    }
  ] : statsSummaries.length > 0 ? [
    // Fallback to existing viewmodel data
    {
      title: "Total Sales",
      value: `$${(statsSummaries[0] as any)?.total_sales || 0}`,
      change: "Data from existing API",
      changeType: "positive" as const,
      icon: "💰",
      color: "bg-blue-500"
    },
    {
      title: "Orders Today", 
      value: (statsSummaries[0] as any)?.orders_today || 0,
      change: "Data from existing API",
      changeType: "positive" as const,
      icon: "📦",
      color: "bg-green-500"
    },
    {
      title: "Low Stock",
      value: (statsSummaries[0] as any)?.low_stock_count || 0,
      change: "Data from existing API",
      changeType: "neutral" as const,
      icon: "⚠️",
      color: "bg-orange-500"
    },
    {
      title: "Active Orders",
      value: (statsSummaries[0] as any)?.active_orders || 0,
      change: "Data from existing API", 
      changeType: "neutral" as const,
      icon: "📋",
      color: "bg-purple-500"
    }
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Welcome back, {userName}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Loading your business insights...
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Loading Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Loading Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-32 mb-6"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-24 mb-4"></div>
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                        <div className="h-4 bg-gray-200 rounded flex-1"></div>
                        <div className="h-4 bg-gray-200 rounded w-12"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome back, {userName}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Here's what's happening with your business today
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.length > 0 ? (
            metrics.map((metric, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
                    <p className={`text-sm mt-1 ${
                      metric.changeType === 'positive' ? 'text-green-600' :
                      metric.changeType === 'negative' ? 'text-orange-600' : 'text-gray-600'
                    }`}>
                      {metric.change}
                    </p>
                  </div>
                  <div className={`${metric.color} rounded-lg p-3 text-white`}>
                    {metric.icon}
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Fallback metrics when no data
            [
              { title: "Total Sales", value: "$0", change: "No data", changeType: "neutral", icon: "💰", color: "bg-gray-500" },
              { title: "Orders Today", value: "0", change: "No data", changeType: "neutral", icon: "📦", color: "bg-gray-500" },
              { title: "Low Stock", value: "0", change: "No alerts", changeType: "positive", icon: "⚠️", color: "bg-green-500" },
              { title: "Active Orders", value: "0", change: "No pending", changeType: "neutral", icon: "📋", color: "bg-gray-500" }
            ].map((metric, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
                    <p className={`text-sm mt-1 ${
                      metric.changeType === 'positive' ? 'text-green-600' :
                      metric.changeType === 'negative' ? 'text-orange-600' : 'text-gray-600'
                    }`}>
                      {metric.change}
                    </p>
                  </div>
                  <div className={`${metric.color} rounded-lg p-3 text-white text-2xl`}>
                    {metric.icon}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Chart and Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Revenue Chart */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Last 6 months</span>
                </div>
              </div>
              <div className="h-64">
                {dashboardData?.chart_data.data ? (
                  <div className="h-full flex items-end justify-between space-x-2">
                    {dashboardData.chart_data.data.map((value, index) => (
                      <div key={index} className="flex flex-col items-center flex-1">
                        <div
                          className="bg-blue-500 rounded-t w-full transition-all duration-500 hover:bg-blue-600"
                          style={{ height: `${(value / Math.max(...dashboardData.chart_data.data)) * 200}px` }}
                        ></div>
                        <span className="text-xs text-gray-500 mt-2">{dashboardData.chart_data.labels[index]}</span>
                      </div>
                    ))}
                  </div>
                ) : chartData && Object.keys(chartData).length > 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-6xl mb-4">📊</div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">Chart Data Available</h4>
                      <p className="text-sm text-gray-600">Using existing chart data from viewmodel</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-6xl mb-4">📊</div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No Revenue Data</h4>
                      <p className="text-sm text-gray-600">Start making sales to see your revenue trend</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Items */}
          <div className="space-y-6">
            {/* Top Products */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Products</h3>
              {dashboardData?.top_items && dashboardData.top_items.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.top_items.map((product, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                          {index + 1}
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-900">{product.name}</span>
                      </div>
                      <span className="text-sm text-gray-600">{product.sales} sales</span>
                    </div>
                  ))}
                </div>
              ) : topSellingItemsStats && topSellingItemsStats.length > 0 ? (
                <div className="space-y-3">
                  {topSellingItemsStats.slice(0, 3).map((product, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                          {index + 1}
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-900">{(product as any).name || `Product ${index + 1}`}</span>
                      </div>
                      <span className="text-sm text-gray-600">{(product as any).sales || (product as any).count || 0} sales</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">🏆</div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">No Product Sales Yet</h4>
                  <p className="text-xs text-gray-600">Products will appear here once you start selling</p>
                </div>
              )}
            </div>

            {/* Top Customers */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Customers</h3>
              {dashboardData?.top_customers && dashboardData.top_customers.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.top_customers.map((customer, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-sm font-medium text-green-600">
                          {index + 1}
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-900">{customer.name}</span>
                      </div>
                      <span className="text-sm text-gray-600">{customer.orders} orders</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">👥</div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">No Customer Orders Yet</h4>
                  <p className="text-xs text-gray-600">Customers will appear here once they place orders</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          {dashboardData?.recent_activity && dashboardData.recent_activity.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.recent_activity.map((activity) => (
                <div key={activity.time} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-150">
                  <div className={`w-2 h-2 rounded-full ${
                    activity.type === 'order_created' ? 'bg-blue-500' :
                    activity.type === 'low_stock' ? 'bg-orange-500' : 'bg-green-500'
                  }`}></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.message}</p>
                  </div>
                  <div className="text-xs text-gray-500">{activity.time_ago}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">📝</div>
              <h4 className="text-sm font-medium text-gray-900 mb-1">No Recent Activity</h4>
              <p className="text-xs text-gray-600">Activity will appear here as your business grows</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
